import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import AuthLayout from "./AuthLayout";
import { useAuth } from "./AuthProvider";
import { lookupEmail } from "@vaulzo/core/api/auth";
import { apiErrorDetail } from "@vaulzo/core/http";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputClass =
  "h-12 w-full rounded-xl border border-vaulzo-input-line bg-white px-4 text-base text-vaulzo-ink placeholder:text-vaulzo-subtle outline-none transition-colors focus:border-vaulzo-accent focus:ring-2 focus:ring-vaulzo-accent/25";

const primaryBtnClass =
  "flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-vaulzo-accent text-base font-semibold text-white transition-colors hover:bg-vaulzo-accent-hover disabled:opacity-60";

/**
 * One-door auth: ask for the email first, look it up, then show either
 * the sign-in or the create-password step. Both steps live on this one
 * page — no separate login/signup routes, no modals.
 */
export default function AuthPage() {
  const { login, register } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [step, setStep] = useState("email"); // "email" | "password"
  const [mode, setMode] = useState(null); // "login" | "create" (known after lookup)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Where to go after auth — ProtectedRoute stashes the page the user
  // was originally headed to.
  const from = location.state?.from || "/dashboard";

  const submitEmail = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await lookupEmail(trimmed);
      setEmail(trimmed);
      setMode(res.exists ? "login" : "create");
      setStep("password");
    } catch (err) {
      setError(apiErrorDetail(err));
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") {
        await login({ email, password });
      } else {
        await register({ email, password });
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(apiErrorDetail(err));
      setBusy(false);
    }
  };

  const changeEmail = () => {
    setStep("email");
    setMode(null);
    setPassword("");
    setError("");
  };

  const isLogin = mode === "login";

  return (
    <AuthLayout>
      {step === "email" ? (
        <>
          <h1 className="text-[28px] font-bold tracking-[-0.02em]">Welcome to Vaulzo</h1>
          <p className="mt-2 text-[15px] leading-relaxed text-vaulzo-faint">
            Enter your email, we'll sign you in, or set up your account if you're new.
          </p>

          <form onSubmit={submitEmail} className="mt-8 space-y-4" noValidate>
            <input
              type="email"
              name="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
              autoFocus
              className={inputClass}
            />
            <FieldError message={error} />
            <button type="submit" disabled={busy} className={primaryBtnClass}>
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </>
              )}
            </button>
          </form>
        </>
      ) : (
        <>
          <h1 className="text-[28px] font-bold tracking-[-0.02em]">
            {isLogin ? "Welcome back" : "Create your account"}
          </h1>
          <p className="mt-2 text-[15px] leading-relaxed text-vaulzo-faint">
            {isLogin
              ? "Enter your password to open your dashboard."
              : "You're new here — pick a password to get started."}
          </p>

          {/* The email being used, with a way back to step one */}
          <div className="mt-6 flex items-center justify-between rounded-xl border border-vaulzo-hairline bg-white px-4 py-3">
            <span className="truncate text-sm text-vaulzo-muted">{email}</span>
            <button
              type="button"
              onClick={changeEmail}
              className="ml-3 flex-none text-sm font-semibold text-vaulzo-accent hover:underline"
            >
              Change
            </button>
          </div>

          <form onSubmit={submitPassword} className="mt-4 space-y-4">
            {/* Hidden email field so password managers save the pair */}
            <input type="hidden" name="email" autoComplete="username" value={email} />
            <input
              type="password"
              name="password"
              autoComplete={isLogin ? "current-password" : "new-password"}
              placeholder={isLogin ? "Your password" : "At least 6 characters"}
              required
              minLength={isLogin ? undefined : 6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              autoFocus
              className={inputClass}
            />
            {!isLogin && (
              <p className="text-xs text-vaulzo-fainter">
                Use a strong password you don't reuse on other sites.
              </p>
            )}
            <FieldError message={error} />
            <button type="submit" disabled={busy} className={primaryBtnClass}>
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : isLogin ? (
                "Sign in"
              ) : (
                "Create account"
              )}
            </button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}

function FieldError({ message }) {
  if (!message) return null;
  return (
    <p role="alert" className="text-sm text-vaulzo-danger">
      {message}
    </p>
  );
}
